import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEOHead — reusable per-page meta tag component.
 *
 * Injects <title>, <meta description>, Open Graph, Twitter Card, and
 * canonical link tags per page without duplicating the CSR shell's
 * homepage metadata. Use on every top-level public page.
 *
 * Usage:
 *   <SEOHead
 *     title="Pricing — CARTIEAI"
 *     description="Flat monthly pricing ..."
 *     canonical="https://cartieai.com/pricing"
 *     schema={{ "@context": "https://schema.org", "@type": "Product", ... }}
 *   />
 */
const BASE_URL = 'https://cartieai.com';
const DEFAULT_OG_IMAGE = `${BASE_URL}/images/og-image.png`;

/**
 * Build a backend-rendered OG image URL for a given slug. Returns null
 * when no slug is provided so callers can fall back to the static asset.
 */
const _ogUrlForSlug = (slug) => {
  if (!slug) return null;
  const apiBase = (process.env.REACT_APP_BACKEND_URL || BASE_URL).replace(/\/$/, '');
  return `${apiBase}/api/og/${encodeURIComponent(slug)}.png`;
};

const SEOHead = ({
  title,
  description,
  canonical,
  ogImage,
  ogSlug,
  ogType = 'website',
  noIndex = false,
  schema,
  keywords,
}) => {
  const fullTitle = title?.includes('CARTIEAI') ? title : `${title} | CARTIEAI`;
  // Build a CLEAN canonical URL — strip query strings & fragments so Google
  // doesn't see "?ref=linkedin" and "?utm=twitter" variants as separate pages.
  // This is the #1 fix for "Duplicate, Google chose different canonical than user".
  const _autoCanonical = () => {
    if (typeof window === 'undefined') return BASE_URL;
    const { origin, pathname } = window.location;
    const cleanPath = pathname.replace(/\/+$/, '') || '/';
    return `${origin}${cleanPath}`;
  };
  const canonicalUrl = canonical || _autoCanonical();
  const finalOgImage = ogImage || _ogUrlForSlug(ogSlug) || DEFAULT_OG_IMAGE;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:site_name" content="CARTIEAI" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={finalOgImage} />
      <meta name="twitter:site" content="@cartieai" />
      <meta name="twitter:creator" content="@cartieai" />

      {/* Optional structured data (JSON-LD) — accepts a single schema object
          OR an array of schema objects so a page can declare e.g. Organization
          + SoftwareApplication + FAQPage at once for richer rich-results. */}
      {schema && (Array.isArray(schema) ? schema : [schema]).map((s, i) => (
        <script key={`ld-${i}`} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;
